import React, { useState, useEffect, ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { getBackgroundTheme, getBorders } from '../style';
import Threads from '../modules/mail/Threads';
import Conversation from '../modules/mail/Conversation';
import NoMessages from '../atoms/mail/NoMessages';
import ReminderModal from '../atoms/mail/ReminderModal';
import SkeletonLoader from '../atoms/SkeletonLoader';
import { disableBodyScroll, clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import { getBrowserDetails, removeBodyScroll, isDarkMode } from '../utils/helpers';
import { messagingReminderEnabled } from '../config/Master';
import TrackLoadingToMixpanel from '../components/common/TrackLoadingToMixPanel';
import PWAMessagePageBanner from '../components/pwa/PWAMessagePageBanner';
import Optional from '../atoms/Optional';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { visitMessagesPage } from '../actions/commonActions';
import breakpoints from '../theme/material/breakpoints';
import { useMediaQuery, useTheme } from '@material-ui/core';

const MailContainer = styled('div')({
  [breakpoints.up(769)]: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '20px 15px 50px 20px',
  },
  [breakpoints.down(768.05)]: {
    margin: '0 10px',
  },
});

const InnerMailContainer = styled('div')(() => ({
  borderRadius: 3,
  backgroundColor: getBackgroundTheme().block,
  height: 'inherit',
  width: '100%',
  display: 'flex',
  ...getBorders().DEFAULT,
}));

const ThreadsContainer = styled('div')<{ openConversation: boolean; setWidth: number }>(
  props => ({
    width: '100%',
    [breakpoints.up(769)]: {
      overflow: 'hidden',
      display: 'flex',
      width: props.setWidth ? props.setWidth : 355,
      flexDirection: 'column',
      alignItems: 'flex-start',
      height: `calc(${getBrowserDetails().isIpad && screen.height > 1024 ? '87vh' : '100vh'} - 137px)`,
      borderRadius: '10px 0px 0px 10px',
      borderTop: getBorders().PRIMARY,
      borderBottom: getBorders().PRIMARY,
      borderLeft: getBorders().PRIMARY,
      background: isDarkMode() ? '#404040' : '#DEDEDE',
    },
  }),
  ({ openConversation }) => ({
    [breakpoints.down(768.05)]: {
      display: openConversation ? 'none' : 'block',
    },
  })
);

const ConvoContainer = styled('div')<{
  hideConversation: boolean;
  openConversation: boolean;
  showPwaInstallPromptBanner: boolean;
  setWidth: number;
}>(
  ({ setWidth }) => ({
    [breakpoints.up(769)]: {
      display: 'block',
      width: setWidth ? `calc(100% - ${setWidth}px)` : 'inherit',
      borderRadius: '0px 10px 10px 0px',
      borderTop: getBorders().PRIMARY,
      borderRight: getBorders().PRIMARY,
      borderBottom: getBorders().PRIMARY,
    },
  }),
  ({ hideConversation }) => ({
    [breakpoints.down(768.05)]: {
      display: hideConversation ? 'block' : 'none',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      position: hideConversation ? 'relative' : 'fixed',
      paddingBottom: hideConversation ? '0px' : '100px',
      backgroundColor: 'inherit',
    },
  }),
  ({ openConversation, showPwaInstallPromptBanner }) => ({
    [breakpoints.down(768.05)]: {
      position: openConversation ? 'fixed' : 'absolute',
      marginTop: openConversation ? 0 : showPwaInstallPromptBanner ? 225 : 122,
      zIndex: openConversation ? 9999 : 995,
    },
  })
);

const isMessagingModalReminderEnabled = messagingReminderEnabled;

interface MailProps {
  reminderStatus: any;
  showPwaInstallPromptBanner: boolean;
}

const Mail = ({ reminderStatus, showPwaInstallPromptBanner }: MailProps): ReactElement => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const fetchingConversation = useSelector((state: RootState) => state.mail.fetchingConversation);
  const visitMessages = useSelector((state: RootState) => state.common.visitMessages);
  const conversationState = useSelector((state: RootState) => state.mail.conversation);
  const conversation = conversationState && Array.isArray(conversationState) ? conversationState[0] : conversationState;
  const threads = useSelector((state: RootState) => state.mail.threads);
  const fetchingThreads = useSelector((state: RootState) => state.mail.fetchingThreads);
  const order = useSelector((state: RootState) => state.mail.order);

  const [reminderPopup, setReminderPopup] = useState(reminderStatus);
  const deviceHeight = window.innerHeight;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1024.05)); // Mobile breakpoint (up to 'sm')
  const isTablet = useMediaQuery(theme.breakpoints.between(712, 835));

  useEffect(() => {
    if (visitMessages && deviceHeight > 1024 && getBrowserDetails().isIpad) {
      dispatch(visitMessagesPage());
    }
  }, [visitMessages, deviceHeight]);

  useEffect(() => {
    if (reminderPopup) {
      const modalOpen = document.getElementsByClassName('ReactModal__Body--open')[0];
      const overlayAfterOpen = document.getElementsByClassName('ReactModal__Overlay--after-open')[0];
      const modalContent = document.getElementsByClassName('ReactModal__Content ReactModal__Content--after-open')[0];

      setTimeout(() => {
        modalOpen && disableBodyScroll(modalOpen);
        overlayAfterOpen && disableBodyScroll(overlayAfterOpen);
        modalContent && enableBodyScroll(modalContent);
        if (document.getElementsByClassName('ReactModal__Body--open').length > 0)
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          document.getElementsByClassName('ReactModal__Body--open')[0].style.overflowY = 'hidden';
      }, 1000);
    } else {
      clearAllBodyScrollLocks();
      removeBodyScroll();
    }
  }, [reminderPopup]);

  useEffect(() => {
    setReminderPopup(reminderStatus !== 0);
  }, [reminderStatus]);

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const pagination = _find(['meta', 'pagination'], threads);
  const canLoadMoreThreads = pagination.current_page < pagination.total_pages || false;
  const noThreads = threads?.collection && threads?.collection.length === 0;

  const setWidth = isMobile && isTablet ? 280 : 360;

  return (
    <MailContainer>
      <InnerMailContainer>
        <ThreadsContainer openConversation={Boolean(conversation.uid)} setWidth={setWidth}>
          <Threads
            order={order || 'recent'}
            fetching={fetchingThreads}
            openThreadHashId={params.hashId}
            canLoadMore={canLoadMoreThreads}
            openConversation={Boolean(conversation.id || conversation.uid)}
            showPwaInstallPromptBanner={showPwaInstallPromptBanner}
            themeDarkMode={isDarkMode()}
          />
        </ThreadsContainer>
        <ConvoContainer
          hideConversation={Boolean(conversation.id || conversation.uid) || noThreads}
          openConversation={Boolean(conversation.id || conversation.uid)}
          showPwaInstallPromptBanner={showPwaInstallPromptBanner}
          setWidth={setWidth}
        >
          {(conversation.id || conversation.uid) && (
            <div style={{ height: '100%' }}>
              <PWAMessagePageBanner customStyle={{ paddingTop: 20 }} />
              <Conversation showPwaInstallPromptBanner={showPwaInstallPromptBanner} />
            </div>
          )}
          <Optional show={!fetchingThreads && (!threads.collection || !noThreads) && !conversation.uid}>
            <SkeletonLoader width="100%" />
          </Optional>
          {(fetchingThreads || fetchingConversation) && <TrackLoadingToMixpanel page="Conversations" />}
          {!fetchingThreads && (!threads.collection || noThreads) && !params.hashId && (
            <NoMessages onClick={() => navigate('/search')} />
          )}
        </ConvoContainer>
      </InnerMailContainer>
      {isMessagingModalReminderEnabled && !fetchingConversation && !noThreads && <ReminderModal />}
    </MailContainer>
  );
};

export default Mail;
